import {
  UserInfoUserRole,
  UserInfoUserType,
  UserOnboardingProgressResponseDto,
  UserOnboardingProgressStepResponseDto,
} from "@/lib/api/interfaces/user-profile.interface";
import { cn } from "@/lib/utils/cn";
import { hasRolePermission, isInternalUser } from "@/lib/utils/role-manager";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
} from "@/util-components/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/ui-components/ui/sidebar";
import {
  Swords,
  ChevronUp,
  Contact,
  Home,
  LogOut,
  Settings,
  Shield,
  User2,
  Users,
  UsersRound,
  Flag,
  Zap,
  Coins,
  Rocket,
  AlertCircle,
  CreditCard,
  Clock,
  Ban,
  HelpCircle,
  X,
  Settings2,
  LayoutGrid,
  CircleDashed,
  Check,
} from "lucide-react";
import { useContext, useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Context } from "./context";
import { EXCLUDE_SIDEBAR_PATHS } from "./root-wrapper";
import { useChangelogNotification } from "@/hooks/use-changelog-notification";
import { NewVersionDialog } from "@/util-components/dialog/NewVersionDialog";
import { useSubscriptions } from "@/lib/hooks/use-subscriptions";
import { motion, AnimatePresence } from "framer-motion";
import { appConfig } from "@/config/app.config";

// --- Types ---

interface SidebarItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  displayEnd?: boolean;
  isExternal?: boolean;
}

// --- Navigation Config ---

const sidebarHome = {
  title: "Home",
  url: "/",
  icon: Home,
};

const sidebarTeamsInternal = {
  title: "Vereine",
  url: "/vereine",
  icon: Shield,
};

const sidebarMyTeam = {
  title: "REPLACE",
  url: "/vereine",
  icon: Shield,
  displayEnd: true,
};

const sidebarTeamsCustomer = {
  title: "Mannschaften",
  url: "/mannschaften",
  icon: UsersRound,
};

const sidebarPostGenerator = {
  title: "Post Generator",
  url: "/post-generator",
  icon: Zap,
};

const sidebarAdmin = {
  title: "Admin",
  url: "/admin",
  icon: Settings2,
};

const sidebarUsers = {
  title: "Nutzer",
  url: "/users",
  icon: Users,
  displayEnd: true,
};

const sidebarPlayers = {
  title: "Spieler",
  url: "/spieler",
  icon: Contact,
};

const sidebarSponsors = {
  title: "Sponsoring",
  url: "/sponsoring/sponsoren",
  icon: Coins,
};

const sidebarMatches = {
  title: "Spiele",
  url: "/spiele",
  icon: Swords,
};

const sidebarOpponents = {
  title: "Gegner",
  url: "/gegner",
  icon: Flag,
};

const bottomMenuSettings = {
  title: "Einstellungen",
  url: "/settings",
  icon: Settings,
};

const bottomMenuHelp = {
  title: "Hilfeportal",
  url: appConfig.links.helpPortal,
  icon: HelpCircle,
  isExternal: true,
};

const bottomMenuChangelog = {
  title: "Was ist neu?",
  url: "/changelog",
  icon: Rocket,
};

const bottomMenuSignOut = {
  title: "Abmelden",
  url: "/sign-out",
  icon: LogOut,
};

function formatDepartmentLabel(key?: string): string {
  if (!key) return "-";
  switch (key) {
    case "generally":
      return "Allgemein";
    case "soccer":
      return "Fußball";
    default:
      return key.charAt(0).toUpperCase() + key.slice(1);
  }
}

// --- Components ---

function getBillingStatusConfig(status?: string, accessUntil?: Date) {
  if (!status || status === 'active') return null;

  if (status === 'trialing') {
    if (accessUntil) {
      const now = new Date();
      const daysLeft = Math.ceil((accessUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      // Nur anzeigen, wenn die Testphase in 5 Tagen oder weniger abläuft
      if (daysLeft > 5) {
        return null;
      }
      
      return {
        label: daysLeft > 0 ? `Testphase (${daysLeft} Tage)` : 'Testphase endet',
        color: 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 hover:text-amber-700 border-amber-200/50',
        icon: Clock,
      };
    }

    return {
      label: 'Testphase',
      color: 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 hover:text-amber-700 border-amber-200/50',
      icon: Clock,
    };
  }

  switch (status) {
    case 'past_due':
      return {
        label: 'Überfällig',
        color: 'bg-red-500/10 text-red-600 hover:bg-red-500/20 hover:text-red-700 border-red-200/50',
        icon: AlertCircle,
      };
    case 'grace_period':
      return {
        label: 'Zahlung offen',
        color: 'bg-orange-500/10 text-orange-600 hover:bg-orange-500/20 hover:text-orange-700 border-orange-200/50',
        icon: AlertCircle,
      };
    case 'canceled':
      return {
        label: 'Gekündigt',
        color: 'bg-gray-500/10 text-gray-600 hover:bg-gray-500/20 hover:text-gray-700 border-gray-200/50',
        icon: Ban,
      };
    case 'inactive':
      return {
        label: 'Inaktiv',
        color: 'bg-gray-500/10 text-gray-600 hover:bg-gray-500/20 hover:text-gray-700 border-gray-200/50',
        icon: Ban,
      };
    default:
      return {
        label: 'Status prüfen',
        color: 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 hover:text-blue-700 border-blue-200/50',
        icon: CreditCard,
      };
  }
}

function BillingStatusBadge({ config }: { config: NonNullable<ReturnType<typeof getBillingStatusConfig>> }) {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip={config.label}
          className={cn(
            "relative overflow-hidden group/billing transition-all duration-200 border backdrop-blur-md opacity-60 saturate-75 group-hover/sidebar:opacity-100 group-hover/sidebar:saturate-100",
            config.color,
            "group-data-[collapsible=icon]:justify-center"
          )}
        >
          <Link 
            to="/vereine/billing" 
            className="flex items-center gap-3 w-full h-full"
          >
            {/* Shimmer Hover Effect */}
            <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden rounded-[inherit] opacity-0 group-hover/billing:opacity-100 transition-opacity duration-200">
              <div className="absolute -left-[65%] top-0 h-full w-[55%] -skew-x-[30deg] bg-gradient-to-r from-transparent via-white/80 to-transparent transition-transform duration-700 ease-out group-hover/billing:translate-x-[230%]" />
            </div>
            
            {/* Subdued animated background to give a living feel when not hovering */}
            <motion.div 
              className="absolute inset-0 pointer-events-none opacity-[0.02] transition-opacity duration-500 group-hover/billing:opacity-0"
              animate={{ 
                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
              }}
              transition={{ 
                duration: 8, 
                repeat: Infinity, 
                ease: "linear" 
              }}
              style={{
                backgroundImage: "linear-gradient(270deg, transparent, currentColor, transparent)",
                backgroundSize: "200% 100%"
              }}
            />
            
            <motion.div
              className="relative z-10"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <config.icon className="size-4 shrink-0 opacity-90 transition-opacity duration-500 group-hover/billing:opacity-100" />
            </motion.div>
            <div className="flex flex-col flex-1 leading-none group-data-[collapsible=icon]:hidden relative z-10">
              <span className="font-medium text-sm opacity-90 transition-opacity duration-200 group-hover/billing:opacity-100">
                {config.label}
              </span>
            </div>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

function CircularProgressIcon({
  progressPercentage,
  className,
  celebrate = false,
  celebrationKey = 0,
}: {
  progressPercentage: number;
  className?: string;
  celebrate?: boolean;
  celebrationKey?: number;
}) {
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const safeProgress = Math.max(0, Math.min(100, progressPercentage));
  const dashOffset = circumference * (1 - safeProgress / 100);

  return (
    <motion.div
      className={cn(
        "relative flex items-center justify-center text-primary",
        className
      )}
      animate={
        celebrate
          ? {
              scale: [1, 1.18, 0.94, 1.08, 1],
              rotate: [0, -10, 8, -4, 0],
              borderRadius: [
                "9999px",
                "42% 58% 60% 40% / 44% 38% 62% 56%",
                "58% 42% 45% 55% / 52% 63% 37% 48%",
                "46% 54% 52% 48% / 41% 57% 43% 59%",
                "9999px",
              ],
            }
          : undefined
      }
      transition={{ duration: 1.1, ease: "easeInOut", times: [0, 0.24, 0.48, 0.76, 1] }}
    >
      <AnimatePresence>
        {celebrate && (
          <>
            <motion.div
              key={`progress-ring-a-${celebrationKey}`}
              className="pointer-events-none absolute inset-0 rounded-full border border-emerald-400/50"
              initial={{ opacity: 0.6, scale: 0.85 }}
              animate={{ opacity: 0, scale: 1.7 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            />
            <motion.div
              key={`progress-ring-b-${celebrationKey}`}
              className="pointer-events-none absolute inset-[2px] rounded-full border border-emerald-300/40"
              initial={{ opacity: 0.4, scale: 0.95 }}
              animate={{ opacity: 0, scale: 1.45 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.05, delay: 0.08, ease: "easeOut" }}
            />
            {[
              "left-1/2 -top-1",
              "-right-1 top-1/2",
              "left-1/3 -bottom-1",
              "-left-1 top-1/3",
            ].map((position, index) => (
              <motion.span
                key={`progress-spark-${celebrationKey}-${index}`}
                className={cn("pointer-events-none absolute size-1.5 rounded-full bg-emerald-400/80", position)}
                initial={{ opacity: 0, scale: 0.2, x: "-50%", y: "-50%" }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0.2, 1.35, 0.45],
                  x: ["-50%", index % 2 === 0 ? "-120%" : "15%"],
                  y: ["-50%", index < 2 ? "-135%" : "5%"],
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, delay: index * 0.05, ease: "easeOut" }}
              />
            ))}
          </>
        )}
      </AnimatePresence>
      <svg viewBox="0 0 36 36" className="size-full -rotate-90">
        <circle cx="18" cy="18" r={radius} fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="4" />
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
    </motion.div>
  );
}

function getOnboardingSummary(progress: UserOnboardingProgressResponseDto): string {
  return `${progress.completedStepsCount} von ${progress.totalSteps} abgeschlossen`;
}

function getOnboardingHeadline(progress: UserOnboardingProgressResponseDto): string {
  return `${progress.currentStep.title}`;
}

function OnboardingStepRow({
  step,
  onClick,
}: {
  step: UserOnboardingProgressStepResponseDto;
  onClick?: () => void;
}) {
  return (
    <DropdownMenuItem
      asChild
      className={cn(
        "cursor-pointer rounded-md px-3 py-2 outline-none transition-all duration-200",
        step.completed
          ? "text-muted-foreground focus:bg-accent/50 focus:text-muted-foreground"
          : "text-foreground font-medium focus:bg-accent focus:text-foreground"
      )}
    >
      <Link to={step.url} onClick={onClick} className="flex w-full items-center gap-3">
        <div className="flex size-5 shrink-0 items-center justify-center">
          {step.completed ? <Check className="size-4 text-emerald-500" /> : <CircleDashed className="size-4 text-muted-foreground" />}
        </div>
        <span className={cn("flex-1 text-sm", step.completed && "line-through opacity-70")}>{step.title}</span>
      </Link>
    </DropdownMenuItem>
  );
}

function SidebarOnboardingMenu({
  progress,
  celebrate = false,
  celebrationKey = 0,
}: {
  progress: UserOnboardingProgressResponseDto;
  celebrate?: boolean;
  celebrationKey?: number;
}) {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="default"
              tooltip={getOnboardingHeadline(progress)}
              className="group text-foreground transition-all duration-200 hover:text-foreground"
            >
              <CircularProgressIcon
                progressPercentage={progress.progressPercentage}
                className="size-5 shrink-0 text-foreground transition-colors group-hover:text-foreground"
                celebrate={celebrate}
                celebrationKey={celebrationKey}
              />
              <motion.div
                className="flex flex-1 items-center justify-between overflow-hidden group-data-[collapsible=icon]:hidden"
                animate={celebrate ? { y: [0, -3, 0], scale: [1, 1.02, 1] } : undefined}
                transition={{ duration: 0.55, ease: "easeOut" }}
              >
                <span className="truncate font-medium text-foreground transition-colors group-hover:text-foreground">{getOnboardingHeadline(progress)}</span>
                <span className="ml-2 text-xs font-medium text-foreground">{progress.progressPercentage}%</span>
              </motion.div>
              <ChevronUp className="ml-auto size-4 rotate-180 text-foreground transition-transform group-data-[state=open]:rotate-0 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="right"
            className="w-[300px] min-w-[300px] rounded-xl border-border/60 p-2 m-2 shadow-lg"
            sideOffset={12}
          >
            <DropdownMenuLabel className="p-3">
              <div className="flex items-start gap-3">
                <CircularProgressIcon
                  progressPercentage={progress.progressPercentage}
                  className="size-8 shrink-0 text-primary"
                  celebrate={celebrate}
                  celebrationKey={celebrationKey}
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">Erste Schritte</p>
                  <p className="mt-1 text-xs font-normal text-muted-foreground">{getOnboardingSummary(progress)}</p>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1 bg-border/50" />
            <DropdownMenuGroup className="space-y-1">
              {progress.steps.map((step) => (
                <OnboardingStepRow key={step.key} step={step} />
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/**
 * Main application sidebar component.
 * 
 * Handles navigation, user profile, and department switching (for customers).
 * Displays changelog notification badge if a new version is available.
 * Adapts content based on user role (Admin vs Customer) and user type.
 */
const AppSidebar = () => {
  const { user, currentDepartment, availableDepartments, setCurrentDepartment } = useContext(Context);
  const location = useLocation();
  const { isMobile } = useSidebar();
  const audience = (user && isInternalUser(user.userType)) ? 'internal' : 'customer';
  const { hasNewVersion, markAsSeen } = useChangelogNotification(audience);
  const subscription = useSubscriptions();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileOnboardingOpen, setIsMobileOnboardingOpen] = useState(false);
  const [isOnboardingCelebrating, setIsOnboardingCelebrating] = useState(false);
  const [onboardingCelebrationKey, setOnboardingCelebrationKey] = useState(0);
  const previousOnboardingSignatureRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileOnboardingOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const isCustomer = user?.userType === UserInfoUserType.CUSTOMER_USER;
  const onboardingProgress =
    user && isCustomer && hasRolePermission(user.userRole, UserInfoUserRole.ADMIN)
      ? user.onboardingProgress
      : undefined;

  useEffect(() => {
    const nextSignature = onboardingProgress
      ? `${onboardingProgress.completedStepsCount}:${onboardingProgress.currentStep.key}`
      : undefined;
    const previousSignature = previousOnboardingSignatureRef.current;

    if (nextSignature && previousSignature && nextSignature !== previousSignature) {
      setOnboardingCelebrationKey((current) => current + 1);
      setIsOnboardingCelebrating(true);

      const timeout = window.setTimeout(() => {
        setIsOnboardingCelebrating(false);
      }, 1800);

      previousOnboardingSignatureRef.current = nextSignature;
      return () => window.clearTimeout(timeout);
    }

    previousOnboardingSignatureRef.current = nextSignature;
    return undefined;
  }, [onboardingProgress]);

  if (EXCLUDE_SIDEBAR_PATHS.includes(location.pathname) || !user) {
    return null;
  }

  const billingStatusConfig = isCustomer ? getBillingStatusConfig(subscription.status, subscription.accessUntil) : null;
  const sidebarItems: SidebarItem[] = [];

  // Logic to build sidebar items
  sidebarItems.push(sidebarHome);

  if (hasRolePermission(user.userRole, UserInfoUserRole.ADMIN)) {
    if (isInternalUser(user.userType)) {
      sidebarItems.push(sidebarTeamsInternal);
      sidebarItems.push(sidebarUsers);
      sidebarItems.push(sidebarPostGenerator);
      sidebarItems.push(sidebarAdmin);
    }
  }

  const bottomMenuItems: SidebarItem[] = [];

  if (isCustomer) {
    if (currentDepartment !== "generally") {
      sidebarItems.push(sidebarTeamsCustomer);
      sidebarItems.push(sidebarOpponents);
      sidebarItems.push(sidebarPlayers);
      sidebarItems.push(sidebarMatches);
      sidebarItems.push(sidebarSponsors);
      sidebarItems.push(sidebarPostGenerator);
      sidebarItems.push({ ...sidebarMyTeam, title: user.organizationName });
    }
    bottomMenuItems.push(bottomMenuSettings);
  }

  bottomMenuItems.push(bottomMenuHelp);
  bottomMenuItems.push(bottomMenuChangelog);
  bottomMenuItems.push(bottomMenuSignOut);

  const mainItems = sidebarItems.filter((item) => !item.displayEnd);
  const bottomItems = sidebarItems.filter((item) => item.displayEnd);

  // Helper for active state
  const isActive = (url: string) => {
    if (url === "/") return location.pathname === url;
    return location.pathname === url || location.pathname.startsWith(`${url}/`);
  };

  return (
    <>
      {/* Mobile Top Bar */}
      {isMobile && (
        <header className="fixed top-0 left-0 right-0 z-50 flex h-16 w-full items-center justify-between border-b bg-background px-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Link to="/">
               <motion.img 
                  src="/logo.png" 
                  alt="SportyPost" 
                  className="h-8 w-auto" 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
               />
            </Link>
          </div>
          <div className="flex items-center gap-2">
              <button 
                 onClick={() => setIsMobileMenuOpen(true)}
                 className="flex size-10 items-center justify-center rounded-md hover:bg-accent focus:bg-accent focus:text-accent-foreground outline-none transition-colors"
                 aria-label="Menü öffnen"
              >
                 <LayoutGrid className="size-5" />
              </button>
          </div>
        </header>
      )}

      {/* Mobile Full Screen Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: "2%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "2%" }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed inset-0 z-[100] flex flex-col bg-background"
          >
            {/* Header */}
            <div className="flex h-16 shrink-0 items-center justify-between px-4 border-b border-border/40">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                 <img src="/logo.png" alt="SportyPost" className="h-8 w-auto" />
              </Link>
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex size-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                aria-label="Menü schließen"
              >
                 <X className="size-5" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 py-6">
              
              {/* Department Switcher */}
              {isCustomer && availableDepartments && availableDepartments.length > 2 && (
                <div className="mb-6">
                  <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Abteilung</p>
                  <div className="flex flex-wrap gap-2 px-2">
                    {availableDepartments.map(dep => (
                      <button
                        key={dep}
                        onClick={() => {
                           if (setCurrentDepartment) setCurrentDepartment(dep);
                           setIsMobileMenuOpen(false);
                        }}
                        className={cn(
                           "rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 border",
                           currentDepartment === dep 
                             ? "bg-primary/10 text-primary border-primary/20 shadow-sm" 
                             : "bg-card text-muted-foreground border-border/40 shadow-sm hover:bg-accent/50 hover:text-foreground"
                        )}
                      >
                        {formatDepartmentLabel(dep)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Main Grid Navigation */}
              <div className="mb-8">
                 <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Navigation</p>
                 <div className="grid grid-cols-2 gap-3 px-2">
                   {mainItems.map((item) => {
                     const active = isActive(item.url);
                     return (
                       <Link 
                         key={item.title} 
                         to={item.url}
                         onClick={() => setIsMobileMenuOpen(false)}
                         className={cn(
                           "flex flex-col items-center justify-center gap-3 rounded-xl p-4 text-center transition-all duration-200 active:scale-[0.98]",
                           active 
                             ? "bg-primary/10 text-primary border border-primary/20 shadow-sm" 
                             : "bg-card border border-border/40 text-muted-foreground shadow-sm hover:bg-accent/50 hover:text-foreground"
                         )}
                       >
                         <item.icon className={cn("size-6", active && "text-primary")} />
                         <span className="text-sm font-medium">{item.title}</span>
                       </Link>
                     );
                   })}
                 </div>
              </div>

              {/* Management List Navigation */}
              {bottomItems.length > 0 && (
                <div className="mb-8">
                   <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Management</p>
                   <div className="flex flex-col gap-2 px-2">
                     {bottomItems.map((item) => {
                       const active = isActive(item.url);
                       return (
                         <Link 
                           key={item.title} 
                           to={item.url}
                           onClick={() => setIsMobileMenuOpen(false)}
                           className={cn(
                             "flex items-center gap-4 rounded-xl p-3 transition-all duration-200 active:scale-[0.98]",
                             active 
                               ? "bg-primary/10 text-primary font-medium border border-primary/20 shadow-sm" 
                               : "bg-card border border-border/40 text-muted-foreground shadow-sm hover:bg-accent/50 hover:text-foreground"
                           )}
                         >
                           <div className={cn("flex size-10 items-center justify-center rounded-lg", active ? "bg-primary/20" : "bg-muted/50")}>
                             <item.icon className={cn("size-5", active && "text-primary")} />
                           </div>
                           <span className="text-sm font-medium">{item.title}</span>
                         </Link>
                       );
                     })}
                   </div>
                </div>
              )}

              {onboardingProgress && (
                <div className="mb-8">
                  <p className="mb-3 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Erste Schritte</p>
                  <div className="flex flex-col gap-2 px-2">
                    <button
                      type="button"
                      onClick={() => setIsMobileOnboardingOpen((current) => !current)}
                      className={cn(
                        "flex items-center gap-4 rounded-xl p-3 bg-card border border-border/40 text-muted-foreground shadow-sm hover:bg-accent/50 hover:text-foreground transition-all duration-200 active:scale-[0.98]",
                        isOnboardingCelebrating && "border-primary/30 bg-primary/[0.04] shadow-[0_14px_40px_-24px_rgba(34,197,94,0.65)]"
                      )}
                    >
                      <motion.div
                        className="flex size-10 items-center justify-center rounded-lg bg-muted/50"
                        animate={
                          isOnboardingCelebrating
                            ? {
                                scale: [1, 1.08, 0.98, 1.03, 1],
                                borderRadius: [
                                  "0.75rem",
                                  "36% 64% 58% 42% / 40% 42% 58% 60%",
                                  "0.95rem",
                                  "42% 58% 52% 48% / 58% 45% 55% 42%",
                                  "0.75rem",
                                ],
                              }
                            : undefined
                        }
                        transition={{ duration: 1, ease: "easeInOut", times: [0, 0.24, 0.5, 0.76, 1] }}
                      >
                        <CircularProgressIcon
                          progressPercentage={onboardingProgress.progressPercentage}
                          className="size-5"
                          celebrate={isOnboardingCelebrating}
                          celebrationKey={onboardingCelebrationKey}
                        />
                      </motion.div>
                      <div className="flex-1 text-left min-w-0">
                        <motion.span
                          className="block text-sm font-medium truncate"
                          animate={
                            isOnboardingCelebrating
                              ? {
                                  y: [0, -3, 0],
                                  scale: [1, 1.015, 1],
                                }
                              : undefined
                          }
                          transition={{ duration: 0.6, ease: "easeOut" }}
                        >
                          {getOnboardingHeadline(onboardingProgress)}
                        </motion.span>
                      </div>
                      <motion.span
                        className="text-xs font-medium text-muted-foreground"
                        animate={isOnboardingCelebrating ? { scale: [1, 1.12, 1], y: [0, -2, 0] } : undefined}
                        transition={{ duration: 0.55, ease: "easeOut" }}
                      >
                        {onboardingProgress.progressPercentage}%
                      </motion.span>
                      <ChevronUp
                        className={cn(
                          "size-4 shrink-0 transition-transform duration-200 ml-1",
                          isMobileOnboardingOpen ? "rotate-0" : "rotate-180"
                        )}
                      />
                    </button>
                    {isMobileOnboardingOpen && (
                      <div className="flex flex-col gap-1 mt-1 pl-4">
                        {onboardingProgress.steps.map((step) => (
                          <Link
                            key={step.key}
                            to={step.url}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-accent/50"
                          >
                            <div className="flex size-6 shrink-0 items-center justify-center">
                              {step.completed ? <Check className="size-4 text-emerald-500" /> : <CircleDashed className="size-4 text-muted-foreground" />}
                            </div>
                            <span className={cn("flex-1 text-sm font-medium", step.completed ? "text-muted-foreground line-through opacity-70" : "text-foreground")}>{step.title}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bottom Menu Items (Settings, Help, Changelog, Logout) */}
              <div className="mb-4 px-2">
                <div className="rounded-xl bg-card border border-border/40 overflow-hidden divide-y divide-border/40 shadow-sm">
                  {bottomMenuItems.map((item) => {
                     const isChangelog = item.url === "/changelog";
                     const isLogout = item.url === "/sign-out";
                     
                     const Content = () => (
                        <div className={cn(
                           "flex items-center gap-4 p-4 transition-colors active:bg-accent/50",
                           isLogout && "text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20",
                           !isLogout && "text-muted-foreground hover:bg-muted hover:text-foreground",
                           isChangelog && hasNewVersion && "bg-blue-50/50 text-blue-600"
                        )}>
                           <item.icon className={cn("size-5 shrink-0", isLogout && "text-red-600", isChangelog && hasNewVersion && "text-blue-600")} />
                           <span className={cn("text-sm font-medium flex-1", isLogout && "text-red-600")}>{item.title}</span>
                           {isChangelog && hasNewVersion && (
                             <span className="ml-auto rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white uppercase tracking-widest shadow-sm">
                               Neu
                             </span>
                           )}
                        </div>
                     );

                     return item.isExternal ? (
                        <a key={item.title} href={item.url} target="_blank" rel="noopener noreferrer" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                           <Content />
                        </a>
                     ) : (
                        <Link key={item.title} to={item.url} className="block" onClick={() => setIsMobileMenuOpen(false)}>
                           <Content />
                        </Link>
                     );
                  })}
                </div>
              </div>
            </div>

            {/* Profile Footer */}
            <div className="shrink-0 border-t border-border/40 bg-muted/20 p-4 safe-area-bottom">
               <div className="flex items-center gap-3 px-2">
                  <div className={cn(
                    "flex size-12 shrink-0 items-center justify-center rounded-xl font-bold text-sm overflow-hidden border border-border/40 shadow-sm",
                    user.logoUrl ? "bg-white dark:bg-zinc-900" : "bg-muted/60 text-muted-foreground"
                  )}>
                      {user.logoUrl ? (
                         <img src={user.logoUrl} alt={user.organizationName} className="h-full w-full object-contain p-1" />
                      ) : (
                         <>{user.firstName.charAt(0)}{user.lastName.charAt(0)}</>
                      )}
                   </div>
                   <div className="flex flex-col flex-1 overflow-hidden">
                      <span className="truncate text-sm font-semibold text-foreground">{user.firstName} {user.lastName}</span>
                      <span className="truncate text-xs text-muted-foreground">{user.organizationName || user.userRole}</span>
                   </div>
               </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Sidebar collapsible="icon" className="group/sidebar border-r border-border bg-sidebar">
        <SidebarHeader className="py-4">
          <div className="flex items-center justify-center px-1 py-2">
            <Link to="/" className="block">
              <motion.img 
                src="/logo.png" 
                alt="logo" 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                className={cn(
                  "h-auto transition-all duration-300", 
                  "group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:object-contain",
                  "w-full"
                )} 
              />
            </Link>
          </div>
        </SidebarHeader>

        <SidebarContent>
          {/* Department Switcher (Customer Only) */}
          {isCustomer && availableDepartments && availableDepartments.length > 2 && (
            <div className="px-2 py-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton 
                     size="lg"
                     className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground border border-input shadow-sm group transition-all duration-200 hover:shadow-md"
                  >
                    <motion.div 
                       className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"
                       whileHover={{ scale: 1.1, rotate: -5 }}
                       whileTap={{ scale: 0.9 }}
                       transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    >
                      <User2 className="size-4" />
                    </motion.div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-semibold uppercase transition-colors group-hover:text-primary">{formatDepartmentLabel(currentDepartment)}</span>
                        <span className="truncate text-xs text-muted-foreground">{availableDepartments.length} Abteilungen</span>
                    </div>
                    <ChevronUp className="ml-auto rotate-180 transition-transform group-data-[state=open]:rotate-0" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-[--radix-popper-anchor-width] min-w-[260px] p-2"
                >
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                    Abteilung wechseln
                  </div>
                  <div className="space-y-1">
                    {availableDepartments.map((dep) => {
                      const isSelected = currentDepartment === dep;
                      return (
                        <DropdownMenuItem
                          key={dep}
                          className={cn(
                            "cursor-pointer flex items-center justify-between py-2.5 px-3 rounded-md transition-colors group",
                            isSelected ? "bg-primary/5 text-primary font-medium" : "hover:bg-muted"
                          )}
                          onClick={() => setCurrentDepartment && setCurrentDepartment(dep)}
                        >
                          <div className="flex items-center gap-3">
                            <motion.div 
                              className={cn(
                                "size-2 rounded-full",
                                isSelected ? "bg-primary" : "bg-muted-foreground/30 group-hover:bg-primary/50"
                              )} 
                              animate={{ scale: isSelected ? 1.5 : 1 }}
                              transition={{ type: "spring", stiffness: 400, damping: 10 }}
                            />
                            <span className="transition-colors group-hover:text-foreground">{formatDepartmentLabel(dep)}</span>
                          </div>
                          {isSelected && (
                            <motion.div 
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full"
                            >
                              Aktiv
                            </motion.div>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          <SidebarGroup>
            <SidebarGroupLabel className="uppercase tracking-wider text-xs font-semibold text-muted-foreground/70 px-4 mt-2 mb-1">
              Plattform
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <AnimatePresence>
                {mainItems.map((item) => {
                  const active = isActive(item.url);
                  return (
                  <SidebarMenuItem key={item.title} className="relative">
                    {active && (
                      <motion.div
                        layoutId="active-nav-item"
                        className="absolute inset-0 rounded-md bg-primary/10"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "spring", stiffness: 350, damping: 30 }}
                      />
                    )}
                    <SidebarMenuButton 
                        asChild 
                        isActive={active}
                        tooltip={item.title}
                        size="default"
                        className={cn(
                          "transition-all duration-200 relative z-10 group",
                          active && "bg-transparent hover:bg-transparent data-[active=true]:bg-transparent"
                        )}
                    >
                      <Link to={item.url} className="flex items-center gap-3">
                        <motion.div
                          whileHover={{ scale: 1.15, rotate: active ? 0 : -5 }}
                          whileTap={{ scale: 0.9 }}
                          transition={{ type: "spring", stiffness: 400, damping: 10 }}
                        >
                          <item.icon className={cn(
                            "size-5 transition-colors", 
                            active ? "text-primary" : "text-foreground group-hover:text-foreground"
                          )} />
                        </motion.div>
                        <span className={cn(
                          "font-medium transition-colors",
                          active ? "text-primary" : "text-foreground group-hover:text-foreground"
                        )}>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )})}
                </AnimatePresence>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {bottomItems.length > 0 && (
            <>
              <SidebarSeparator className="my-2 opacity-50" />
              <SidebarGroup>
                 <SidebarGroupLabel className="uppercase tracking-wider text-xs font-semibold text-muted-foreground/70 px-4 mt-1 mb-1">
                    Management
                 </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <AnimatePresence>
                    {bottomItems.map((item) => {
                      const active = isActive(item.url);
                      return (
                      <SidebarMenuItem key={item.title} className="relative">
                        {active && (
                          <motion.div
                            layoutId="active-nav-item"
                            className="absolute inset-0 rounded-md bg-primary/10"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                          />
                        )}
                        <SidebarMenuButton 
                            asChild 
                            isActive={active}
                            tooltip={item.title}
                            className={cn(
                              "transition-all duration-200 relative z-10 group",
                              active && "bg-transparent hover:bg-transparent data-[active=true]:bg-transparent"
                            )}
                        >
                          <Link to={item.url} className="flex items-center gap-3">
                            <motion.div
                              whileHover={{ scale: 1.15, rotate: active ? 0 : -5 }}
                              whileTap={{ scale: 0.9 }}
                              transition={{ type: "spring", stiffness: 400, damping: 10 }}
                            >
                              <item.icon className={cn(
                                "size-5 transition-colors", 
                                active ? "text-primary" : "text-foreground group-hover:text-foreground"
                              )} />
                            </motion.div>
                            <span className={cn(
                               "font-medium transition-colors",
                               active ? "text-primary" : "text-foreground group-hover:text-foreground"
                            )}>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )})}
                    </AnimatePresence>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </>
          )}

          {onboardingProgress && (
            <SidebarGroup className="mt-auto">
              <SidebarGroupContent>
                <SidebarOnboardingMenu
                  progress={onboardingProgress}
                  celebrate={isOnboardingCelebrating}
                  celebrationKey={onboardingCelebrationKey}
                />
              </SidebarGroupContent>
            </SidebarGroup>
          )}

        </SidebarContent>

        <SidebarFooter className="p-2 border-t border-border/50 gap-2">
          {billingStatusConfig && <BillingStatusBadge config={billingStatusConfig} />}

          {hasNewVersion && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton 
                asChild 
                isActive={isActive(bottomMenuChangelog.url)}
                onClick={() => markAsSeen()}
                className={cn(
                  "transition-all duration-200 group",
                  "bg-blue-50/50 text-blue-600 hover:bg-blue-50 hover:text-blue-700 border border-blue-100/50 hover:shadow-sm",
                  "group-data-[collapsible=icon]:justify-center"
                )}
                tooltip={bottomMenuChangelog.title}
              >
                <Link to={bottomMenuChangelog.url} className="flex items-center gap-3">
                  <motion.div
                    whileHover={{ scale: 1.15, rotate: 10 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <bottomMenuChangelog.icon className="size-4 shrink-0 transition-colors group-hover:text-blue-700" />
                  </motion.div>
                  <span className="font-semibold text-[11px] uppercase tracking-wider group-data-[collapsible=icon]:hidden">
                    {bottomMenuChangelog.title}
                  </span>
                  {hasNewVersion && (
                     <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                        className="ml-auto size-2 rounded-full bg-blue-600 group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:top-1 group-data-[collapsible=icon]:right-1" 
                     />
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          )}

          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton 
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group transition-all duration-200 hover:bg-sidebar-accent/50"
                  >
                     <motion.div 
                        className={cn(
                          "flex aspect-square size-8 items-center justify-center rounded-lg font-bold text-xs overflow-hidden border border-border/40 transition-colors",
                          user.logoUrl ? "bg-white dark:bg-zinc-900" : "bg-muted/60 text-muted-foreground"
                        )}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                     >
                        {user.logoUrl ? (
                           <img src={user.logoUrl} alt={user.organizationName} className="h-full w-full object-contain p-1" />
                        ) : (
                           <span>{user.firstName.charAt(0)}{user.lastName.charAt(0)}</span>
                        )}
                     </motion.div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold text-foreground transition-colors group-hover:text-foreground">{user.firstName} {user.lastName}</span>
                      <span className="truncate text-xs text-foreground transition-colors group-hover:text-foreground">{user.organizationName || user.userRole}</span>
                    </div>
                    <ChevronUp className="ml-auto size-4 text-foreground transition-transform group-hover:text-foreground group-data-[state=open]:rotate-180" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="top"
                  align="end"
                  className="w-[--radix-popper-anchor-width] min-w-[240px] rounded-xl p-1 shadow-lg border-border/40"
                >
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-3 px-2 py-2.5">
                       <div className={cn(
                          "flex size-9 items-center justify-center rounded-lg font-bold text-sm overflow-hidden border border-border/40",
                          user.logoUrl ? "bg-white dark:bg-zinc-900" : "bg-muted/60 text-muted-foreground"
                        )}>
                            {user.logoUrl ? (
                               <img src={user.logoUrl} alt={user.organizationName} className="h-full w-full object-contain p-1" />
                            ) : (
                               <>{user.firstName.charAt(0)}{user.lastName.charAt(0)}</>
                            )}
                         </div>
                       <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-semibold text-foreground">{user.firstName} {user.lastName}</span>
                          <span className="truncate text-xs text-muted-foreground">{user.organizationName || user.userRole}</span>
                       </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-border/40" />
                  <DropdownMenuGroup>
                    {bottomMenuItems.filter(item => item.url !== "/sign-out").map((item) => {
                      const isChangelog = item.url === "/changelog";
                      return (
                        <DropdownMenuItem
                          key={item.title}
                          className={cn(
                            "group flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-md transition-all duration-200 outline-none",
                            (isChangelog && hasNewVersion)
                               ? "bg-blue-50/50 text-blue-600 focus:bg-blue-50 focus:text-blue-700 font-medium" 
                               : "text-muted-foreground focus:bg-accent focus:text-accent-foreground",
                            isChangelog && hasNewVersion && "border border-blue-100/50"
                          )}
                          onClick={() => {
                             if (isChangelog) markAsSeen();
                          }}
                          asChild
                        >
                          {item.isExternal ? (
                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-3">
                              <motion.div
                                whileHover={{ scale: 1.15, rotate: isChangelog ? 10 : -5 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                              >
                                <item.icon className={cn(
                                  "size-4 transition-colors",
                                  (isChangelog && hasNewVersion) ? "text-blue-600" : "group-hover:text-foreground group-focus:text-foreground"
                                )} />
                              </motion.div>
                              <span className="font-medium flex-1 transition-colors group-hover:text-foreground group-focus:text-foreground">{item.title}</span>
                            </a>
                          ) : (
                            <Link to={item.url} className="w-full flex items-center gap-3">
                              <motion.div
                                whileHover={{ scale: 1.15, rotate: isChangelog ? 10 : -5 }}
                                whileTap={{ scale: 0.9 }}
                                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                              >
                                <item.icon className={cn(
                                  "size-4 transition-colors",
                                  (isChangelog && hasNewVersion) ? "text-blue-600" : "group-hover:text-foreground group-focus:text-foreground"
                                )} />
                              </motion.div>
                              <span className="font-medium flex-1 transition-colors group-hover:text-foreground group-focus:text-foreground">{item.title}</span>
                              {isChangelog && hasNewVersion && (
                                <motion.div 
                                  initial={{ scale: 0 }}
                                  animate={{ scale: [1, 1.2, 1] }}
                                  transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                  className="size-2 rounded-full bg-blue-600 shadow-sm" 
                                />
                              )}
                            </Link>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator className="bg-border/40" />
                  <DropdownMenuItem
                    className="group flex items-center gap-3 px-3 py-2.5 cursor-pointer rounded-md transition-all duration-200 outline-none text-red-600 focus:bg-red-50 focus:text-red-700 dark:text-red-500 dark:focus:bg-red-500/10 dark:focus:text-red-400"
                    asChild
                  >
                    <Link to="/sign-out" className="w-full flex items-center gap-3">
                      <motion.div
                        whileHover={{ scale: 1.15, x: -2 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                      >
                        <LogOut className="size-4" />
                      </motion.div>
                      <span className="font-medium flex-1">Abmelden</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <NewVersionDialog audience={audience} />
    </>
  );
};

export default AppSidebar;
